import type { ExpiringAgreementModel } from '@kk/api/modules/leasing';
import useBreakpoint from '@kk/shared/hooks/useBreakpoint';
import { formatDate } from '@kk/shared/utils/date';
import { formatCurrency } from '@kk/shared/utils/numbers';
import Checkbox from '@kk/ui/components/Checkbox';
import { InlineNotification } from '@kk/ui/components/Notifications/InlineNotification';
import Spinner from '@kk/ui/components/Spinner';
import {
  createTableColumnHelper,
  type TableColumnDef,
} from '@kk/ui/components/Table';
import Table from '@kk/ui/components/Table/Table';
import Widget from '@kk/ui/components/Widget';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAnonymousAyvensExpiringAgreements,
  useAnonymousNormalExpiringAgreements,
  useAyvensExpiringAgreements,
  useNormalExpiringAgreements,
} from '@/api/hooks/useExpiringAgreements';
import TextLink from '@/components/TextLink/TextLink';
import { useExpiringFlow } from './ExpiringFlowContext';

export default function ExpiringAgreementsInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useBreakpoint('(min-width: 768px)');
  const { setParamA, setParamB, setParamP, updateAgreements } =
    useExpiringFlow();
  const [searchParams] = useSearchParams();

  const paramA = searchParams.get('a') ?? '';
  const paramB = searchParams.get('b') ?? '';
  const paramP = searchParams.get('p') ?? '';

  useEffect(() => {
    setParamA(paramA);
    setParamB(paramB);
    setParamP(paramP);
  }, [paramA, paramB, paramP, setParamA, setParamB, setParamP]);

  const isAnonymous = !!paramA && !!paramB && !!paramP;

  const normalAgreements = useNormalExpiringAgreements({
    enabled: !isAnonymous,
  });
  const ayvensAgreements = useAyvensExpiringAgreements({
    enabled: !isAnonymous,
  });
  const anonymousNormalAgreements = useAnonymousNormalExpiringAgreements(
    paramA,
    paramB,
    paramP,
  );
  const anonymousAyvensAgreements = useAnonymousAyvensExpiringAgreements(
    paramA,
    paramB,
    paramP,
  );

  const agreementsNearingExpiry = isAnonymous
    ? (anonymousNormalAgreements.data ?? [])
    : (normalAgreements.data ?? []);

  const isLoadingAgreements = isAnonymous
    ? anonymousNormalAgreements.isLoading
    : normalAgreements.isLoading;

  const isErrorAgreements = isAnonymous
    ? anonymousNormalAgreements.isError
    : normalAgreements.isError;

  const leaseplanCarsNearingExpiry = isAnonymous
    ? (anonymousAyvensAgreements.data ?? [])
    : (ayvensAgreements.data ?? []);

  const isLoadingLeaseplan = isAnonymous
    ? anonymousAyvensAgreements.isLoading
    : ayvensAgreements.isLoading;

  const isErrorLeaseplan = isAnonymous
    ? anonymousAyvensAgreements.isError
    : ayvensAgreements.isError;

  const [selectedAgreements, setSelectedAgreements] = useState<
    ExpiringAgreementModel[]
  >([]);
  const [selectedLeaseplans, setSelectedLeaseplans] = useState<
    ExpiringAgreementModel[]
  >([]);

  const onAgreementChange = (
    agreement: ExpiringAgreementModel,
    isChecked: boolean,
  ) => {
    setSelectedAgreements((prev) => {
      if (isChecked) {
        return [...prev, agreement];
      }
      return prev.filter((a) => a.contractNo !== agreement.contractNo);
    });
  };

  const onLeaseplanChange = (
    agreement: ExpiringAgreementModel,
    isChecked: boolean,
  ) => {
    setSelectedLeaseplans((prev) => {
      if (isChecked) {
        return [...prev, agreement];
      }
      return prev.filter((a) => a.contractNo !== agreement.contractNo);
    });
  };

  const onAgreementsClick = () => {
    updateAgreements(selectedAgreements);
    navigate('/expiringagreements/flow/selectaction');
  };

  const onLeaseplansClick = () => {
    updateAgreements(selectedLeaseplans);
    navigate('/expiringagreements/flow/ayvensselectaction');
  };

  const agreementsColumns = useMemo<
    TableColumnDef<ExpiringAgreementModel>[]
  >(() => {
    const columnHelper = createTableColumnHelper<ExpiringAgreementModel>();

    const checkboxColumn = isDesktop
      ? [
          columnHelper.display({
            id: 'actions',
            size: 1,
            cell: (props) => {
              const agreement = props.row.original;
              const isChecked = selectedAgreements.some(
                (a) => a.contractNo === agreement.contractNo,
              );
              return (
                <Checkbox
                  name="agreement"
                  checked={isChecked}
                  onChange={(e) =>
                    onAgreementChange(
                      agreement,
                      (e.target as HTMLInputElement).checked,
                    )
                  }
                />
              );
            },
          }),
        ]
      : [];

    const desktopColumns = isDesktop
      ? [
          columnHelper.accessor('registrationNo', {
            header: t('common.registrationNumber'),
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
          // columnHelper.accessor('residualValueInclVAT', {
          //   header: t('common.remainingValueIncludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          // columnHelper.accessor('residualValueExclVAT', {
          //   header: t('common.remainingValueExcludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          columnHelper.accessor('residualValueExclVAT', {
            header: t('common.residualValue'),
            cell: (info) =>
              formatCurrency(info.getValue(), { appendCurrency: true }),
            enableColumnFilter: false,
            meta: {
              rightAlign: true,
            },
          }),
          columnHelper.accessor('periodEnd', {
            header: t('common.expirationDate'),
            cell: (info) => formatDate(info.getValue()),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
        ]
      : [];

    const visibleColumns = [
      ...checkboxColumn,
      columnHelper.accessor('contractNo', {
        header: t('common.agreement'),
        cell: (info) => {
          return (
            <TextLink
              to={`/agreements/leasingagreements/${info.getValue()}`}
              text={info.getValue() ?? ''}
              iconType="loan-alt"
              solid={true}
            />
          );
        },
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
      columnHelper.accessor('contractDescription', {
        header: t('common.description'),
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
      ...desktopColumns,
    ];

    return columnHelper.columns(visibleColumns);
  }, [t, isDesktop, selectedAgreements]);

  const leaseplanColumns = useMemo<
    TableColumnDef<ExpiringAgreementModel>[]
  >(() => {
    const columnHelper = createTableColumnHelper<ExpiringAgreementModel>();

    const checkboxColumn = isDesktop
      ? [
          columnHelper.display({
            id: 'actions',
            size: 1,
            cell: (props) => {
              const agreement = props.row.original;
              const isChecked = selectedLeaseplans.some(
                (a) => a.contractNo === agreement.contractNo,
              );
              return (
                <Checkbox
                  name="leaseplan"
                  checked={isChecked}
                  onChange={(e) =>
                    onLeaseplanChange(
                      agreement,
                      (e.target as HTMLInputElement).checked,
                    )
                  }
                />
              );
            },
          }),
        ]
      : [];

    const desktopColumns = isDesktop
      ? [
          columnHelper.accessor('registrationNo', {
            header: t('common.registrationNumber'),
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
          // columnHelper.accessor('residualValueInclVAT', {
          //   header: t('common.remainingValueIncludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          // columnHelper.accessor('residualValueExclVAT', {
          //   header: t('common.remainingValueExcludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          // columnHelper.accessor('netInvestmentLCY', {
          //   header: t('common.residualValue'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          columnHelper.accessor('periodEnd', {
            header: t('common.expirationDate'),
            cell: (info) => formatDate(info.getValue()),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
        ]
      : [];

    const visibleColumns = [
      ...checkboxColumn,
      columnHelper.accessor('contractNo', {
        header: t('common.agreement'),
        cell: (info) => {
          return (
            <TextLink
              to={`/agreements/leasingagreements/${info.getValue()}`}
              text={info.getValue() ?? ''}
              iconType="loan-alt"
              solid={true}
            />
          );
        },
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
      columnHelper.accessor('contractDescription', {
        header: t('common.description'),
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
      ...desktopColumns,
    ];

    return columnHelper.columns(visibleColumns);
  }, [t, isDesktop, selectedLeaseplans]);

  return (
    <div className="space-y-8 p-8">
      <Widget
        title={t(`common.agreementsUntilExpiry`)}
        icon="spreadsheet"
        actions={[
          {
            text: t('button.export'),
            theme: 'default',
            icon: 'arrow-down-underlined',
            disabled: isLoadingAgreements,
          },
          {
            text: t('button.handleSelected'),
            theme: 'primary',
            disabled: selectedAgreements.length === 0,
            onClick: onAgreementsClick,
          },
        ]}
        description={t(`expiringAgreements.description.agreements`)}
      >
        {isLoadingAgreements ? (
          <div className="flex justify-center py-8">
            <Spinner size="medium" />
          </div>
        ) : isErrorAgreements ? (
          <InlineNotification
            type="danger"
            size="medium"
            headline={t('error.api.title')}
            message={t('error.api.message')}
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Checkbox
                name="selectAllExpiringAgreements"
                labelElement={
                  <span className="typ-ui">{t('checkbox.selectAll')}</span>
                }
                checked={
                  agreementsNearingExpiry.length > 0 &&
                  selectedAgreements.length === agreementsNearingExpiry.length
                }
                onChange={(e) => {
                  const isChecked = (e.target as HTMLInputElement).checked;
                  setSelectedAgreements(
                    isChecked ? agreementsNearingExpiry : [],
                  );
                }}
              />
            </div>

            {agreementsNearingExpiry.length ? (
              <Table<ExpiringAgreementModel>
                stickyHeader
                sortable
                variant="relaxed"
                columns={agreementsColumns}
                data={agreementsNearingExpiry}
                paginated
                pageSize={10}
                initialSorting={[{ id: 'periodEnd', desc: false }]}
                className="border-neutral-10 bg-neutral-0 kunde:h-auto mb-4! rounded-md border"
              />
            ) : (
              <p className="typ-body">{t(`table.noData.expiringAgreements`)}</p>
            )}
          </>
        )}
      </Widget>

      <Widget
        title={t(`common.ayvensAgreementsExpiring`)}
        icon="spreadsheet"
        actions={[
          {
            text: t('button.export'),
            theme: 'default',
            icon: 'arrow-down-underlined',
            disabled: isLoadingLeaseplan,
          },
          {
            text: t('button.handleSelected'),
            theme: 'primary',
            disabled: selectedLeaseplans.length === 0,
            onClick: onLeaseplansClick,
          },
        ]}
        description={t(`expiringAgreements.description.leaseplan`)}
      >
        {isLoadingLeaseplan ? (
          <div className="flex justify-center py-8">
            <Spinner size="medium" />
          </div>
        ) : isErrorLeaseplan ? (
          <InlineNotification
            type="danger"
            size="medium"
            headline={t('error.api.title')}
            message={t('error.api.message')}
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Checkbox
                name="selectAllLeaseplans"
                labelElement={
                  <span className="typ-ui">{t('checkbox.selectAll')}</span>
                }
                checked={
                  leaseplanCarsNearingExpiry.length > 0 &&
                  selectedLeaseplans.length ===
                    leaseplanCarsNearingExpiry.length
                }
                onChange={(e) => {
                  const isChecked = (e.target as HTMLInputElement).checked;
                  setSelectedLeaseplans(
                    isChecked ? leaseplanCarsNearingExpiry : [],
                  );
                }}
              />
            </div>

            {leaseplanCarsNearingExpiry.length ? (
              <Table<ExpiringAgreementModel>
                stickyHeader
                sortable
                variant="relaxed"
                columns={leaseplanColumns}
                data={leaseplanCarsNearingExpiry}
                paginated
                pageSize={10}
                initialSorting={[{ id: 'periodEnd', desc: false }]}
                className="border-neutral-10 bg-neutral-0 kunde:h-auto mb-4! rounded-md border"
              />
            ) : (
              <p className="typ-body">{t(`table.noData.expiringAgreements`)}</p>
            )}
          </>
        )}
      </Widget>
    </div>
  );
}
